// OAuth connections for MCP clients (issue #272): the consent screen at
// `/connect` and Settings → Connected apps.
//
// The authorization server keeps everything sensitive (state, PKCE challenge,
// redirect_uri) server-side; the browser only ever sees the opaque pending
// request id. That id is also the only thing this module persists, in
// sessionStorage: it arrives as `/connect?request=<id>`, is moved out of the
// address bar before analytics can record it (utils/oauthRequestParam.ts), and
// survives the login / register detour there.
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders } from './authService';
import type { ToolAccess } from '../utils/toolAccess';

/** sessionStorage key holding the pending request id across the login detour. */
export const OAUTH_REQUEST_KEY = 'basiqr.oauthRequest';

/** `req_` + 22 base64url chars (128-bit), as issued by `/oauth/authorize`. */
export const OAUTH_REQUEST_ID_PATTERN = /^req_[A-Za-z0-9_-]{22}$/;

export const isValidOAuthRequestId = (id: unknown): id is string =>
  typeof id === 'string' && OAUTH_REQUEST_ID_PATTERN.test(id);

/**
 * The SPA route of the consent screen. In-app navigations go here WITHOUT the
 * id (it is read from sessionStorage), so no pageview ever records it.
 */
export const CONNECT_PATH = '/connect';

/**
 * The pending request id, or null. Anything stored that isn't a well-formed id
 * is dropped rather than trusted — it ends up in an API path.
 */
export const getPendingOAuthRequest = (): string | null => {
  try {
    const raw = sessionStorage.getItem(OAUTH_REQUEST_KEY);
    if (raw === null) return null;
    if (isValidOAuthRequestId(raw)) return raw;
    sessionStorage.removeItem(OAUTH_REQUEST_KEY);
    return null;
  } catch {
    return null; // sessionStorage unavailable (privacy mode)
  }
};

export const setPendingOAuthRequest = (requestId: string): void => {
  if (!isValidOAuthRequestId(requestId)) return;
  try {
    sessionStorage.setItem(OAUTH_REQUEST_KEY, requestId);
  } catch {
    // sessionStorage unavailable — the user can restart from the app.
  }
};

export const clearPendingOAuthRequest = (): void => {
  try {
    sessionStorage.removeItem(OAUTH_REQUEST_KEY);
  } catch {
    // nothing to clear
  }
};

export const hasPendingOAuthRequest = (): boolean => getPendingOAuthRequest() !== null;

// ---- API shapes (contract §A "Consent + grants API") ------------------------

export interface OAuthRequestInfo {
  requestId: string;
  client: {
    /** Display name from the client's pinned metadata document, e.g. "Claude". */
    name: string;
    /** Hostname of the client_id URL, e.g. "claude.ai". */
    host: string;
    /** Host the browser is sent back to after the decision. */
    redirectHost: string;
  };
  /** Space-separated OAuth scopes the client asked for, e.g. "mcp:read mcp:write". */
  scope: string;
  /**
   * The client asked for `mcp:write` (#294). When false (or absent), only
   * read tools can be approved.
   */
  writeRequested: boolean;
  account: { email: string };
  eligible: boolean;
  ineligibleReason?: 'api_access_disabled';
  expiresAt: string;
}

export interface OAuthDecisionInput {
  approve: boolean;
  /** `'all'` (or absent) = no narrowing; otherwise 1..50 collection ids. */
  collections?: 'all' | string[];
  /** The tools the connection may use (#294); sent with an approval only. */
  toolAccess?: ToolAccess;
}

export interface OAuthGrant {
  grantId: string;
  clientId: string;
  clientName: string;
  clientHost: string;
  /** Absent or empty = every collection the account can read. */
  collectionPermissions?: Record<string, string[]>;
  /** Per-tool permission (#294). Absent = approved before it: legacy read-only. */
  toolAccess?: ToolAccess;
  /** The MCP tool names the connection can use right now. */
  tools: string[];
  createdAt: string;
  lastUsedAt?: string;
  consentExpiresAt: string;
}

export interface UpdatedOAuthGrant {
  grant: OAuthGrant;
  /**
   * Collections the connection is limited to that the account can only read,
   * so write tools won't work on them.
   */
  readOnlyCollections: string[];
}

export type OAuthApiError = Error & { status?: number };

const jsonHeaders = async () => ({
  'Content-Type': 'application/json',
  ...(await getAuthHeaders()),
});

const parse = async (response: Response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || 'Request failed') as OAuthApiError;
    error.status = response.status;
    throw error;
  }
  return data;
};

const requestPath = (requestId: string) => {
  // Never put an unchecked value in the path: `..` survives encodeURIComponent.
  if (!isValidOAuthRequestId(requestId)) {
    const error = new Error('Unknown connection request') as OAuthApiError;
    error.status = 404;
    throw error;
  }
  return `${API_BASE_URL}api/oauth/requests/${encodeURIComponent(requestId)}`;
};

export const getOAuthRequest = async (requestId: string): Promise<OAuthRequestInfo> => {
  const url = requestPath(requestId);
  const res = await fetch(url, { method: 'GET', headers: await jsonHeaders() });
  return (await parse(res)).data as OAuthRequestInfo;
};

/** Records the decision; resolves to the client redirect URL built by the API. */
export const decideOAuthRequest = async (
  requestId: string,
  input: OAuthDecisionInput
): Promise<{ redirectTo: string }> => {
  const url = `${requestPath(requestId)}/decision`;
  const body: OAuthDecisionInput = { approve: input.approve };
  if (input.approve && input.collections !== undefined) body.collections = input.collections;
  if (input.approve && input.toolAccess !== undefined) body.toolAccess = input.toolAccess;
  const res = await fetch(url, {
    method: 'POST',
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  });
  const data = await parse(res);
  const redirectTo = data?.data?.redirectTo;
  if (typeof redirectTo !== 'string' || redirectTo.length === 0) {
    throw new Error('The server did not say where to send you next');
  }
  return { redirectTo };
};

export const listOAuthGrants = async (): Promise<OAuthGrant[]> => {
  const res = await fetch(`${API_BASE_URL}api/oauth/grants`, {
    method: 'GET',
    headers: await jsonHeaders(),
  });
  return (await parse(res)).data?.grants ?? [];
};

export const revokeOAuthGrant = async (grantId: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}api/oauth/grants/${encodeURIComponent(grantId)}`, {
    method: 'DELETE',
    headers: await jsonHeaders(),
  });
  await parse(res);
};

/**
 * Replace a connection's tools (#294). Applies to its next call; Claude may
 * keep showing its old tool list until a new conversation.
 * 404 unknown, 409 no longer usable, 400 with a message.
 */
export const updateOAuthGrantTools = async (
  grantId: string,
  toolAccess: ToolAccess
): Promise<UpdatedOAuthGrant> => {
  const res = await fetch(`${API_BASE_URL}api/oauth/grants/${encodeURIComponent(grantId)}`, {
    method: 'PATCH',
    headers: await jsonHeaders(),
    body: JSON.stringify({ toolAccess }),
  });
  const data = (await parse(res)).data ?? {};
  return {
    grant: data.grant,
    readOnlyCollections: Array.isArray(data.readOnlyCollections) ? data.readOnlyCollections : [],
  };
};

/**
 * Leave the SPA for the client's redirect URI. Only ever called with the
 * `redirectTo` our own decision API returned; refuses anything that isn't
 * http(s) so a malformed value can never run script in this origin.
 * (A wrapper so tests can observe it: jsdom's `window.location` is unforgeable.)
 */
export const redirectToClient = (redirectTo: string): void => {
  let parsed: URL;
  try {
    parsed = new URL(redirectTo);
  } catch {
    throw new Error('The redirect address is not valid');
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error('The redirect address is not valid');
  }
  window.location.assign(redirectTo);
};
